import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import type { CaptainBillingUsageMonth } from "../../types";

import { ChartTooltipContent } from "@/components/ui/v2/chart";
import { cn } from "@/lib/utils/cn";
import { useValueFormatter } from "@/utils/useValueFormatter";

export type CaptainUsageRow = Pick<CaptainBillingUsageMonth, "month" | "replies"> & {
  label: string;
  longLabel: string;
  fromCap: number;
  fromTopUp: number;
  projected: number;
  cap: number;
  isCurrent: boolean;
};

type Series = "fromCap" | "fromTopUp" | "projected" | "cap";

const SWATCH: Record<Series, string> = {
  fromCap: "size-2.5 rounded-[2px] bg-(--color-fromCap)",
  fromTopUp: "size-2.5 rounded-[2px] bg-(--color-fromTopUp)",
  projected: "size-2.5 rounded-[2px] border border-dashed border-(--color-projected)",
  cap: "h-0 w-2.5 border-t-2 border-dashed border-(--color-cap)",
};

export function CaptainUsageTooltip(props: ComponentProps<typeof ChartTooltipContent>) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const row = props.payload?.[0]?.payload as CaptainUsageRow | undefined;
  if (!props.active || !row) return null;

  const series: Series[] = ["fromCap", "fromTopUp", ...(row.projected > 0 ? (["projected"] as const) : []), "cap"];

  return (
    <div className="grid min-w-[260px] gap-2 rounded-lg border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-md">
      <p className="font-medium">{row.longLabel}</p>
      <dl className="grid gap-1.5">
        {series.map((key) => (
          <div key={key} className="flex items-center justify-between gap-6">
            <dt className="flex items-center gap-2 whitespace-nowrap text-muted-foreground">
              <span aria-hidden className={cn("shrink-0", SWATCH[key])} />
              {t(`captainBilling.usage.series.${key}`)}
            </dt>
            <dd className="font-medium tabular-nums">{format(row[key], "decimal")}</dd>
          </div>
        ))}
      </dl>
      <div className="flex items-center justify-between gap-6 border-t pt-2">
        <span className="text-muted-foreground">{t("captainBilling.usage.replies")}</span>
        <span className="font-medium tabular-nums">{format(row.replies, "whole")}</span>
      </div>
    </div>
  );
}
